import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Shield, Lock, ArrowLeft, Smartphone } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminLogin() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [requires2FA, setRequires2FA] = useState(false);
  const navigate = useNavigate();

  const handleLogin = async () => {
    if (!username || !password) {
      toast.error('Please enter credentials');
      return;
    }
    
    if (requires2FA && !totpCode) {
      toast.error('Please enter your 2FA code');
      return;
    }
    
    setLoading(true);
    try {
      const payload = { username, password };
      if (totpCode) {
        payload.totp_code = totpCode;
      }
      const response = await axios.post(`${API}/admin/login`, payload);
      localStorage.setItem('admin_token', response.data.token);
      localStorage.setItem('adminToken', response.data.token); // Backwards compatibility
      toast.success('Admin login successful!');
      navigate('/admin/dashboard');
    } catch (error) {
      let detail = error.response?.data?.detail || 'Invalid credentials';
      
      // Handle Pydantic validation errors (returns array of objects)
      if (Array.isArray(detail)) {
        detail = detail.map(e => e.msg || e.message || 'Validation error').join(', ');
      } else if (typeof detail === 'object') {
        detail = detail.msg || detail.message || JSON.stringify(detail);
      }
      
      // If 2FA is required but not provided
      if (detail === '2FA code required') {
        setRequires2FA(true);
        toast.info('Please enter your 2FA code');
      } else {
        toast.error(String(detail));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div 
      className="min-h-screen flex items-center justify-center p-4"
      style={{ background: 'linear-gradient(135deg, #0A0F2C 0%, #020617 100%)' }}
    >
      {/* Red/purple glow */}
      <div 
        className="absolute top-1/3 left-1/2 -translate-x-1/2 w-96 h-96 rounded-full opacity-20 blur-3xl pointer-events-none"
        style={{ background: 'radial-gradient(circle, #DC2626 0%, transparent 70%)' }}
      />
      
      <Card 
        className="w-full max-w-md border-0 relative z-10"
        style={{ 
          background: 'rgba(255, 255, 255, 0.03)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(255, 255, 255, 0.08)'
        }}
        data-testid="admin-login-card"
      >
        <CardHeader className="text-center pb-2">
          <div className="flex justify-center mb-4">
            <div 
              className="p-3 rounded-full"
              style={{ background: 'linear-gradient(135deg, #DC2626 0%, #7C3AED 100%)' }}
            >
              <Shield className="h-8 w-8 text-white" />
            </div>
          </div>
          <CardTitle 
            className="text-2xl font-bold"
            style={{ color: '#E5E7EB' }}
          >
            Admin Panel
          </CardTitle>
          <CardDescription style={{ color: 'rgba(229, 231, 235, 0.5)' }}>
            Secure access to admin dashboard
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6 pt-4">
          <div className="space-y-2">
            <label className="text-sm font-medium" style={{ color: '#E5E7EB' }}>Username</label>
            <Input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="admin"
              className="bg-white/5 border-white/10 text-white placeholder:text-gray-500 focus:border-red-500"
              data-testid="admin-username-input"
              disabled={requires2FA}
            />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium" style={{ color: '#E5E7EB' }}>Password</label>
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              className="bg-white/5 border-white/10 text-white placeholder:text-gray-500 focus:border-red-500"
              data-testid="admin-password-input"
              onKeyDown={(e) => e.key === 'Enter' && !requires2FA && handleLogin()}
              disabled={requires2FA}
            />
          </div>
          
          {/* 2FA Code Input */}
          {requires2FA && (
            <div className="space-y-2">
              <label className="text-sm font-medium flex items-center gap-2" style={{ color: '#E5E7EB' }}>
                <Smartphone className="h-4 w-4 text-green-500" />
                2FA Code
              </label>
              <Input
                type="text"
                value={totpCode}
                onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="Enter 6-digit code"
                className="bg-white/5 border-white/10 text-white placeholder:text-gray-500 focus:border-green-500 text-center text-xl tracking-widest"
                data-testid="admin-2fa-input"
                maxLength={6}
                onKeyDown={(e) => e.key === 'Enter' && handleLogin()}
                autoFocus
              />
              <p className="text-xs text-center" style={{ color: 'rgba(229,231,235,0.5)' }}>
                Enter the code from your authenticator app
              </p>
            </div>
          )}
          
          <Button 
            onClick={handleLogin} 
            className="w-full h-12 text-white font-semibold"
            style={{ background: requires2FA 
              ? 'linear-gradient(135deg, #22C55E 0%, #16A34A 100%)' 
              : 'linear-gradient(135deg, #DC2626 0%, #7C3AED 100%)' 
            }}
            disabled={loading} 
            data-testid="admin-login-button"
          >
            {loading ? 'Authenticating...' : requires2FA ? 'Verify & Login' : 'Access Dashboard'}
          </Button>
          
          {requires2FA && (
            <Button 
              onClick={() => {
                setRequires2FA(false);
                setTotpCode('');
              }} 
              variant="ghost" 
              className="w-full text-gray-400 hover:text-white hover:bg-white/5"
            >
              Back to credentials
            </Button>
          )}
          
          <Button 
            onClick={() => navigate('/')} 
            variant="ghost" 
            className="w-full text-gray-400 hover:text-white hover:bg-white/5"
            data-testid="back-to-app-button"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to App
          </Button>
          
          <div className="flex items-center justify-center gap-2 text-xs pt-4" style={{ color: 'rgba(229, 231, 235, 0.3)' }}>
            <Lock className="h-3 w-3" />
            Authorized personnel only
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
